import { useCallback, useEffect, useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";

const num = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 });
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const yearStart = () => `${today().slice(0, 4)}-01-01`;
const TYPE = { purchase_in: "sc_purchase", production_in: "sc_prodIn", production_out: "sc_prodOut", sale_out: "sc_sale", adjustment: "sc_adj" };

function Stat({ label, qty, unit, value, testid, strong }) {
  return (
    <div className={`rounded-md border p-3 ${strong ? "border-primary bg-primary/5" : "border-border"}`} data-testid={testid}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="num font-semibold" data-testid={`${testid}-qty`}>{num(qty)} <span className="text-xs font-normal">{unit}</span></p>
      {value !== undefined && <p className="num text-xs text-muted-foreground" data-testid={`${testid}-value`}>{fmtIDR(value)}</p>}
    </div>
  );
}

function Summary({ card, unit, lang }) {
  const T = card.totals;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="stock-card-summary">
      <Stat label={fl("sc_open", lang)} qty={card.opening_qty} unit={unit} value={card.opening_value} testid="stock-card-opening" />
      <Stat label={fl("sc_purchase", lang)} qty={T.purchase_in} unit={unit} testid="stock-card-purchase" />
      <Stat label={fl("sc_prodIn", lang)} qty={T.production_in} unit={unit} testid="stock-card-prodin" />
      <Stat label={fl("sc_prodOut", lang)} qty={-T.production_out} unit={unit} testid="stock-card-prodout" />
      <Stat label={fl("sc_sale", lang)} qty={-T.sale_out} unit={unit} testid="stock-card-sale" />
      <Stat label={fl("sc_close", lang)} qty={card.closing_qty} unit={unit} value={card.closing_value} testid="stock-card-closing" strong />
    </div>
  );
}

function BalanceRow({ label, qtyIn, qtyOut, cost, value, balance, testid }) {
  return (
    <TableRow className="bg-secondary/50 font-semibold" data-testid={testid}>
      <TableCell colSpan={4} className="text-xs">{label}</TableCell>
      <TableCell className="num text-right text-xs">{qtyIn === undefined ? "" : num(qtyIn)}</TableCell>
      <TableCell className="num text-right text-xs">{qtyOut === undefined ? "" : num(qtyOut)}</TableCell>
      <TableCell className="num text-right text-xs">{fmtIDR(cost)}</TableCell>
      <TableCell className="num text-right text-xs">{fmtIDR(value)}</TableCell>
      <TableCell className="num text-right text-xs">{num(balance)}</TableCell>
    </TableRow>
  );
}

function Movements({ card, lang }) {
  return (
    <div className="overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader><TableRow>
          <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("type", lang)}</TableHead><TableHead>{fl("sc_ref", lang)}</TableHead><TableHead>{fl("desc", lang)}</TableHead>
          <TableHead className="text-right">{fl("sc_in", lang)}</TableHead><TableHead className="text-right">{fl("sc_out", lang)}</TableHead>
          <TableHead className="text-right">{fl("sc_cost", lang)}</TableHead><TableHead className="text-right">{fl("sc_value", lang)}</TableHead><TableHead className="text-right">{fl("sc_bal", lang)}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          <BalanceRow label={fl("sc_open", lang)} cost={card.opening_cost} value={card.opening_value} balance={card.opening_qty} testid="stock-card-row-opening" />
          {card.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground text-sm">{fl("empty", lang)}</TableCell></TableRow>}
          {card.rows.map((r) => (
            <TableRow key={r.id} data-testid={`stock-card-row-${r.id}`}>
              <TableCell className="num text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
              <TableCell className="text-xs">{fl(TYPE[r.type] || r.type, lang)}</TableCell>
              <TableCell className="text-xs">{r.ref || "—"}</TableCell>
              <TableCell className="text-xs max-w-[220px] truncate">{r.note}</TableCell>
              <TableCell className="num text-right text-xs text-emerald-700">{r.qty_in ? num(r.qty_in) : ""}</TableCell>
              <TableCell className="num text-right text-xs text-rose-600">{r.qty_out ? num(r.qty_out) : ""}</TableCell>
              <TableCell className="num text-right text-xs">{fmtIDR(r.unit_cost)}</TableCell>
              <TableCell className="num text-right text-xs">{fmtIDR(r.value)}</TableCell>
              <TableCell className="num text-right text-xs font-medium">{num(r.balance)}</TableCell>
            </TableRow>
          ))}
          <BalanceRow label={`${fl("sc_close", lang)} · ${fl("sc_lastPrice", lang)}`} qtyIn={card.qty_in} qtyOut={card.qty_out} cost={card.closing_cost}
            value={card.closing_value} balance={card.closing_qty} testid="stock-card-row-closing" />
        </TableBody>
      </Table>
    </div>
  );
}

export function StockCardDialog({ clientId, product }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ from: yearStart(), to: today() });
  const [card, setCard] = useState(null);
  const load = useCallback(async () => {
    setCard(null);
    const p = new URLSearchParams({ client_id: clientId, product_id: product.id });
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    try { const { data } = await api.get(`/inventory/stock-card?${p}`); setCard(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setCard(false); }
  }, [clientId, product.id, range]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const unit = product.unit || "";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title={fl("sc_title", lang)} data-testid={`product-card-${product.id}`}><ClipboardList className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto space-y-3" data-testid="stock-card-dialog">
        <DialogHeader><DialogTitle>{fl("sc_title", lang)} — {product.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("sc_from", lang)}</label><DateInput className="h-9 w-40" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="stock-card-from" /></div>
          <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("sc_to", lang)}</label><DateInput className="h-9 w-40" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="stock-card-to" /></div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setRange({ from: "", to: "" })} data-testid="stock-card-all">{fl("sc_all", lang)}</Button>
        </div>
        {card === null && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="stock-card-loading">{fl("loading", lang)}</p>}
        {card && <><Summary card={card} unit={unit} lang={lang} /><Movements card={card} lang={lang} />
          <p className="text-[11px] text-muted-foreground">{fl("sc_note", lang)}</p></>}
      </DialogContent>
    </Dialog>
  );
}
